'use client';

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classes from './SegmentedControl.module.scss';

import { cn } from '../../utils';
import { SegmentedControlProps } from './SegmentedControl.types';

type SegmentedControlRootOwnProps = any;

interface SegmentedControlRootProps {
  value?: string;
  defaultValue?: string;
  onValueChange?(value: string): void;
  children: React.ReactNode;
  radius?: string;
  className?: string;
}

const SegmentedControlRoot = React.forwardRef<HTMLDivElement, SegmentedControlRootProps>(
  (props, forwardedRef) => {
    const {
      className,
      children,
      radius,
      value: valueProp,
      defaultValue: defaultValueProp,
      onValueChange: onValueChangeProp,
      ...rootProps
    } = props;

    const [value, setValue] = useControllableState({
      prop: valueProp,
      onChange: onValueChangeProp,
      defaultProp: defaultValueProp,
    });

    return (
      <ToggleGroupPrimitive.Root
        data-radius={radius}
        ref={forwardedRef}
        className={cn(classes['rt-SegmentedControlRoot'], className)}
        onValueChange={(value) => {
          if (value) {
            setValue(value);
          }
        }}
        {...rootProps}
        type='single'
        value={value}
        asChild={false}
        disabled={false}
      >
        {children}
        <div className={cn(classes['rt-SegmentedControlIndicator'])} />
      </ToggleGroupPrimitive.Root>
    );
  }
);

SegmentedControlRoot.displayName = 'SegmentedControl.Root';

interface SegmentedControlItemOwnProps {
  value: string;
}

interface SegmentedControlItemProps extends SegmentedControlItemOwnProps {
  children: React.ReactNode;
  className?: string;
}

const SegmentedControlItem = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlItemProps
>(({ children, className, ...props }, forwardedRef) => (
  <ToggleGroupPrimitive.Item
    ref={forwardedRef}
    className={cn(classes['rt-reset'], classes['rt-SegmentedControlItem'], className)}
    {...props}
    disabled={false}
    asChild={false}
  >
    <span className={classes['rt-SegmentedControlItemSeparator']} />
    <span className={classes['rt-SegmentedControlItemLabel']}>
      <span className={classes['rt-SegmentedControlItemLabelActive']}>{children}</span>
      <span className={classes['rt-SegmentedControlItemLabelInactive']}>{children}</span>
    </span>
  </ToggleGroupPrimitive.Item>
));

SegmentedControlItem.displayName = 'SegmentedControl.Item';

export { SegmentedControlRoot as Root, SegmentedControlItem as Item };
export type {
  SegmentedControlRootProps as RootProps,
  SegmentedControlItemProps as ItemProps,
};

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  items,
  className = '',
  defaultValue,
  onChange,
  ariaLabel,
  ...props
}) => {
  return (
    <SegmentedControlRoot
      data-testid='segmented-control-component'
      className={cn(classes['SegmentedControl'], className)}
      defaultValue={defaultValue}
      onValueChange={onChange}
      aria-label={ariaLabel}
      {...props}
    >
      <>
        {items.map((item) => (
          <SegmentedControlItem key={item.value} value={item.value}>
            {item.label}
          </SegmentedControlItem>
        ))}
      </>
    </SegmentedControlRoot>
  );
};

export default SegmentedControl;
